import { useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageLayout from "../../../shared/ui/layouts/PageLayout/PageLayout";
import { routes } from "../../../shared/config/routes";
import {
  useTaskDetail,
  useUpdateTask,
  useDeleteTask,
  getTaskStatusLabel,
  getTaskStatusColor,
} from "../../../entities/task";
import Loader from "../../../shared/ui/feedback/Loader/Loader";
import { SuccessDialog } from "../../../shared/ui/feedback/SuccessDialog/SuccessDialog";
import DeleteItemDialog from "../../../shared/ui/feedback/DeleteItemDialog/DeleteItemDialog";
import { useSuccessDialog } from "../../../shared/hooks/useSuccessDialog";
import { useDeleteDialog } from "../../../shared/hooks/useDeleteDialog";
import {
  TaskFormWidget,
  toUpdateDto,
  fromTaskDetail,
  type TaskFormValues,
} from "../../../features/aufgaben";

function getTodayKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function AufgabenEditPage() {
  const navigate = useNavigate();
  const { taskId } = useParams<{ taskId: string }>();
  const id = Number(taskId);

  const { data, isLoading } = useTaskDetail(id);
  const updateMutation = useUpdateTask(id);
  const deleteMutation = useDeleteTask();
  const submitRef = useRef<(() => void) | null>(null);
  const pendingAction = useRef<"save" | "plan">("save");
  const { showSuccess, dialogProps } = useSuccessDialog();
  const deleteDialog = useDeleteDialog();

  if (isLoading || !data) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  const isTimeScheduled =
    !!data.plannedStart && new Date(data.plannedStart).getUTCHours() !== 0;

  const handleValidate = (values: TaskFormValues) => {
    if (pendingAction.current === "save" && !values.assignedTechnicianId) {
      return { assignedTechnicianId: "Pflichtfeld" };
    }
    return null;
  };

  const handleSubmit = async (values: TaskFormValues) => {
    const dto = toUpdateDto(values);
    await updateMutation.mutateAsync(dto);

    if (pendingAction.current === "plan") {
      const rawDate = values.plannedStart || "";
      const dayKey = rawDate ? rawDate.substring(0, 10) : getTodayKey();
      navigate(`${routes.aufgaben.plan}?taskId=${id}&day=${dayKey}`);
    } else {
      showSuccess("Aufgabe erfolgreich gespeichert", () =>
        navigate(routes.aufgaben.root)
      );
    }
  };

  const pageTitle = (
    <>
      Aufgabe bearbeiten
      <span style={{ color: getTaskStatusColor(data.status) }}>
        {" "}{getTaskStatusLabel(data.status)}
      </span>
    </>
  );

  return (
    <>
      <PageLayout
        pageTitle={pageTitle}
        scrollable
        back
        backTo={routes.aufgaben.root}
        primaryAction={{
          label: "Speichern",
          onClick: () => {
            pendingAction.current = "save";
            submitRef.current?.();
          },
          loading: updateMutation.isPending,
        }}
        secondaryAction={{
          label: isTimeScheduled ? "Umdisponieren" : "Im Kalender planen",
          onClick: () => {
            pendingAction.current = "plan";
            submitRef.current?.();
          },
          loading: updateMutation.isPending,
        }}
        removeAction={{
          onClick: () =>
            deleteDialog.openDelete("Aufgabe wirklich löschen?", async () => {
              await deleteMutation.mutateAsync(id);
              navigate(routes.aufgaben.root);
            }),
        }}
      >
        <TaskFormWidget
          taskType={data.taskType}
          onSubmit={handleSubmit}
          isLoading={updateMutation.isPending}
          submitRef={submitRef}
          initialValues={fromTaskDetail(data)}
          mode="edit"
          onValidate={handleValidate}
        />
      </PageLayout>

      <SuccessDialog
        open={dialogProps.open}
        onOpenChange={(open) => {
          if (!open) dialogProps.onClose();
        }}
        title={dialogProps.title}
        onClose={dialogProps.onClose}
      />

      <DeleteItemDialog deleteModal={deleteDialog} />
    </>
  );
}
